import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Building2, Plus, Edit, Trash2, Eye, EyeOff, 
  Loader2, Search, Shield, Globe, Phone, Mail, MapPin, Crown,
  ExternalLink, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';
import CenterEditor from './CenterEditor';

// Plan colors and labels
var PLAN_INFO = {
  gratuito: { label: 'Gratuito', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  destacado: { label: 'Destacado', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  premium: { label: 'Premium', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  elite: { label: 'Elite', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
};

export default function AdminCenters() {
  var [centers, setCenters] = useState([]);
  var [filteredCenters, setFilteredCenters] = useState([]);
  var [loading, setLoading] = useState(true);
  var [searchQuery, setSearchQuery] = useState('');
  var [sortBy, setSortBy] = useState('recent');
  var [editorOpen, setEditorOpen] = useState(false);
  var [editingCenter, setEditingCenter] = useState(null);

  useEffect(function() {
    load();
  }, []);

  useEffect(function() {
    var result = centers;
    
    // Filter by search
    if (searchQuery.trim() !== '') {
      var query = searchQuery.toLowerCase();
      result = result.filter(function(c) {
        return (c.name && c.name.toLowerCase().includes(query)) ||
               (c.city && c.city.toLowerCase().includes(query)) ||
               (c.email && c.email.toLowerCase().includes(query));
      });
    }
    
    // Sort
    result = [...result].sort(function(a, b) {
      if (sortBy === 'recent') {
        var dateA = a.created_at || a.updated_at || '';
        var dateB = b.created_at || b.updated_at || '';
        return dateB.localeCompare(dateA);
      } else if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      } else if (sortBy === 'verified') {
        return (b.is_verified ? 1 : 0) - (a.is_verified ? 1 : 0);
      }
      return 0;
    });
    
    setFilteredCenters(result);
  }, [searchQuery, centers, sortBy]);

  async function load() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/admin/centers?limit=500', { withCredentials: true });
      setCenters(res.data.centers || []);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar centros');
    }
    setLoading(false);
  }

  async function handleDelete(id, name) {
    if (!window.confirm('¿Eliminar el centro "' + name + '"? Esta acción no se puede deshacer.')) return;
    try {
      await authAxios.delete(API + '/admin/centers/' + id, { withCredentials: true });
      toast.success('Centro eliminado');
      load();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  }

  async function toggleVisibility(id, currentVisibility) {
    if (!id) {
      toast.error('ID del centro no válido');
      return;
    }
    try {
      console.log('Toggling visibility for center:', id, 'from', currentVisibility, 'to', !currentVisibility);
      await authAxios.put(API + '/admin/centers/' + id, { is_public: !currentVisibility }, { withCredentials: true });
      toast.success(currentVisibility ? 'Centro ocultado' : 'Centro ahora es público');
      await load();
    } catch (e) {
      console.error('Toggle visibility error:', e);
      toast.error('Error al cambiar visibilidad: ' + (e.response?.data?.detail || e.message));
    }
  }

  function openEditor(center) {
    setEditingCenter(center || null);
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditingCenter(null);
  }

  function handleEditorSave() {
    load();
    closeEditor();
  }

  function getPlanBadge(center) {
    var planType = center.plan_type || 'gratuito';
    var info = PLAN_INFO[planType] || PLAN_INFO.gratuito;
    return (
      <Badge className={'text-xs border ' + info.color}>
        <Crown className="w-3 h-3 mr-1" />
        {info.label}
      </Badge>
    );
  }

  function getImageUrl(url) {
    if (!url) return null;
    if (url.includes('.emergent.host/api/') || url.includes('.emergentagent.com/api/')) {
      try { url = new URL(url).pathname; } catch(e) {}
    }
    if (url.startsWith('/api/')) {
      var base = process.env.REACT_APP_BACKEND_URL;
    if (base && !base.includes('localhost') && typeof window !== 'undefined' && !base.includes(window.location.hostname)) {
      base = window.location.origin;
    }
    return (base || '') + url;
    }
    return url;
  }

  // Calculate stats
  var stats = {
    total: centers.length,
    public: centers.filter(function(c) { return c.is_public !== false; }).length,
    verified: centers.filter(function(c) { return c.is_verified; }).length,
    elite: centers.filter(function(c) { return c.plan_level >= 3; }).length,
    premium: centers.filter(function(c) { return c.plan_level === 2; }).length,
    destacado: centers.filter(function(c) { return c.plan_level === 1; }).length
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Buscar centros por nombre, ciudad o email..."
            value={searchQuery}
            onChange={function(e) { setSearchQuery(e.target.value); }}
            className="pl-10 bg-slate-800 border-slate-700 text-white"
            data-testid="search-centers"
          />
        </div>
        <Button 
          onClick={function() { openEditor(null); }}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="new-center-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Centro
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-slate-400 text-xs">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{stats.public}</p>
              <p className="text-slate-400 text-xs">Públicos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.verified}</p>
              <p className="text-slate-400 text-xs">Verificados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-400">{stats.elite}</p>
              <p className="text-slate-400 text-xs">Elite</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-400">{stats.premium}</p>
              <p className="text-slate-400 text-xs">Premium</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.destacado}</p>
              <p className="text-slate-400 text-xs">Destacado</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Centers List */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-400" />
            Centros ({filteredCenters.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCenters.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No hay centros que coincidan con la búsqueda</p>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {filteredCenters.map(function(center) {
                var logoUrl = getImageUrl(center.logo);
                return (
                  <div 
                    key={center.center_id || center._id} 
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                    data-testid={'center-row-' + center.center_id}
                  >
                    <div className="flex gap-4">
                      {/* Image */}
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                        {logoUrl ? (
                          <img 
                            src={logoUrl} 
                            alt={center.name}
                            className="w-full h-full object-cover"
                            onError={function(e) { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-slate-500" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-white font-medium truncate">{center.name}</h4>
                              {center.is_branch && (
                                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                                  Sucursal
                                </Badge>
                              )}
                              {center.is_verified && (
                                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                                  <Shield className="w-3 h-3 mr-1" /> Verificado
                                </Badge>
                              )}
                              {!center.is_public && (
                                <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                                  Oculto
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 mt-1">
                              {getPlanBadge(center)}
                              {center.modality && center.modality.length > 0 && (
                                <span className="text-xs text-slate-500">
                                  {Array.isArray(center.modality) ? center.modality.join(', ') : center.modality}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-400">
                              {center.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {center.city}
                                </span>
                              )}
                              {center.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {center.phone}
                                </span>
                              )}
                              {center.email && (
                                <span className="flex items-center gap-1 truncate max-w-[200px]">
                                  <Mail className="w-3 h-3" /> {center.email}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={function() { toggleVisibility(center.center_id, center.is_public !== false); }}
                              className="text-slate-400 hover:text-white"
                              title={center.is_public !== false ? 'Ocultar' : 'Mostrar'}
                            >
                              {center.is_public !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={function() { openEditor(center); }}
                              className="text-slate-400 hover:text-emerald-400"
                              title="Editar"
                              data-testid={'edit-center-' + center.center_id}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <a 
                              href={'/centros/' + (center.slug || center.center_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-slate-400 hover:text-blue-400 transition-colors"
                              title="Ver perfil público"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={function() { handleDelete(center.center_id, center.name); }}
                              className="text-slate-400 hover:text-red-400"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Center Editor */}
      {editorOpen && (
        <CenterEditor 
          center={editingCenter}
          onClose={closeEditor}
          onSave={handleEditorSave}
        />
      )}
    </div>
  );
}
